<script setup lang="ts">
import { RouterLink } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import { routes } from '@app/router/routes'
import { LEGAL_DOCUMENT_SLUG, LEGAL_INDEX_PATH, legalDocumentPath } from '@modules/legal'

interface FooterLink {
  label: string
  to: string
}

// The legal pages and the contact form are public, so a signed-out reader can
// follow these in place.
const links: FooterLink[] = [
  { label: 'Terms', to: legalDocumentPath(LEGAL_DOCUMENT_SLUG.TERMS) },
  { label: 'Privacy', to: legalDocumentPath(LEGAL_DOCUMENT_SLUG.PRIVACY) },
  { label: 'Policies & Legal', to: LEGAL_INDEX_PATH },
  { label: 'Contact us', to: routes.SUPPORT_PUBLIC_CONTACT },
]

const year = new Date().getFullYear()
</script>

<template>
  <Box is="footer" class="auth-footer">
    <Text type="caption" color="muted" class="auth-footer__copy">© {{ year }} Sliceal</Text>
    <Box class="auth-footer__links">
      <template v-for="(link, index) in links" :key="link.label">
        <Text
          v-if="index > 0"
          is="span"
          type="caption"
          color="muted"
          class="auth-footer__sep"
          aria-hidden="true"
          >•</Text
        >
        <RouterLink :to="link.to" class="auth-footer__link">
          <Text is="span" type="caption" color="muted" class="auth-footer__label">{{
            link.label
          }}</Text>
        </RouterLink>
      </template>
    </Box>
  </Box>
</template>

<style scoped>
.auth-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  border-top: 1px solid var(--p-content-border-color);
  flex-shrink: 0;
  opacity: 0.85;
}

.auth-footer__copy,
.auth-footer__label,
.auth-footer__sep {
  font-size: 11px !important;
}

.auth-footer__links {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.auth-footer__link {
  text-decoration: none;
}

.auth-footer__label {
  transition: color 0.15s;
}

.auth-footer__link:hover .auth-footer__label {
  color: var(--p-primary-600);
}

.auth-footer__sep {
  opacity: 0.4;
}

@media (max-width: 992px) {
  .auth-footer {
    flex-direction: column-reverse;
    gap: 0.5rem;
    padding: 1rem;
  }
}
</style>
